/**
 * Lane C0-TRUEPX-GAME: the one palette the true-pixel clean-up snaps the whole screen to, as ramps per material family
 * (docs/research/TRUE_PIXEL_ART.md, "In the game"). Built once, shared by figures, scenery, buildings and the ground,
 * so everything on screen reads as one pixel-art set.
 *
 * - Figure families come from the master palette (docs/art/palette/main-palette-ref.png: warm white, maroon to red,
 *   gold, dark brown, skin, ink), each extended into a hue-shifted ramp (darks toward violet-blue, lights toward warm
 *   yellow), plus dye families (jade, violet, ochre, steel) so free customization keeps its hues.
 * - Scenery families are the master-look set's (render/masterLook.ts: foliage, timber, stone, slate, blossom, blue).
 * - Ground families are the ground painter's own ramps (render/groundArt.ts `RAMPS`), so the ground snaps to itself.
 *
 * Per tone: its ramp and step, the next lighter / darker tone of the same ramp, and a line tone for the selective
 * outline (a dark, cooler shade of it). Colour maths from tools/truepx/ramps.ts.
 */
import { RAMPS as GROUND_RAMPS } from '../groundArt';

const MASTER: readonly (readonly string[])[] = [
  ['#0e0a10', '#1b1315', '#2a2026', '#3b3038', '#4e4250'], // ink
  ['#2a0a14', '#461420', '#671f2c', '#87232a', '#c4272d', '#e55546', '#f48a6c'], // maroon to red
  ['#5e4c5a', '#84707c', '#ab979e', '#ceb8b8', '#e0cbc9', '#eddfda', '#f9ebe7'], // warm white cloth
  ['#1b1315', '#2c1c1e', '#3b2522', '#573b34', '#7c5f57', '#a08474'], // dark brown, hair, leather
  ['#5e3430', '#8a5444', '#b67858', '#ca9165', '#e4ad8e', '#f2c4aa', '#fdd9c2'], // skin
  ['#3e2a14', '#6a4622', '#9a6732', '#c08c48', '#e4b575', '#f6dca0'], // gold, brass
  ['#0f2a2a', '#1a4644', '#2a6660', '#42897c', '#6cae98', '#a4d4bc'], // jade, teal dye
  ['#1e1430', '#34234e', '#503a70', '#725894', '#9a80b4', '#c8b0d6'], // violet dye
  ['#4a3a10', '#7a6020', '#a88a2c', '#d0b440', '#ecd870', '#fff2b0'], // ochre, yellow dye
  ['#1e2430', '#343e50', '#4e5c70', '#708094', '#9aa8b8', '#c8d2dc'], // steel, blue-grey
  // Measured from today's starter pair (the rig's skin, navy robe, gold trim, steel and off-white), so the commonest
  // figure colours have a ramp of their own instead of the nearest master one.
  ['#8e6450', '#b8927a', '#ceb497', '#e2be9f', '#f6cfac', '#fde2c4'], // rig skin
  ['#0b1228', '#111c3a', '#1d2a4f', '#2b4169', '#425c82', '#6a86aa'], // navy cloth
  ['#6e4e1e', '#9a7430', '#c5993c', '#d6a640', '#f0d27a', '#fbeaa8'], // trim gold
  ['#2a2e40', '#414a65', '#626c84', '#84919f', '#b4b8bc', '#dcd5c6', '#efe7d6'], // steel to off-white
];
const SCENERY: readonly (readonly string[])[] = [
  ['#12261f', '#1f4636', '#2f6048', '#4f8d64', '#86bf7a'], // foliage
  ['#2a1c18', '#4a2e26', '#6a4332', '#8f6445', '#b88c62'], // timber and earth
  ['#2c2a30', '#4a4650', '#6f6a6c', '#988f88', '#c4b8a8'], // stone
  ['#1c2234', '#2c3a56', '#44587a', '#6a82a0', '#98b0c8'], // slate roof
  ['#5a1f3a', '#a9486c', '#d9708f', '#f2a0b9', '#ffd9e4'], // blossom
  ['#1a2a4a', '#284f7d', '#3f83a6', '#7fbcbe', '#cfe4e2'], // blue, water
];

type RGB = readonly [number, number, number];
const fromHex = (h: string): RGB => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as unknown as RGB;
/** Packed as ImageData reads on little-endian machines: 0xAABBGGRR. */
const pack = (r: number, g: number, b: number): number =>
  ((255 << 24) | (Math.round(b) << 16) | (Math.round(g) << 8) | Math.round(r)) >>> 0;

export type Lab = [number, number, number];
export function oklab(r8: number, g8: number, b8: number): Lab {
  const lin = (v: number) => {
    v /= 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const r = lin(r8);
  const g = lin(g8);
  const b = lin(b8);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function toHsl(r8: number, g8: number, b8: number): [number, number, number] {
  const r = r8 / 255;
  const g = g8 / 255;
  const b = b8 / 255;
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const l = (mx + mn) / 2;
  if (mx === mn) return [0, 0, l];
  const d = mx - mn;
  const s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
  const h = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, s, l];
}
function fromHsl(h: number, s: number, l: number): number {
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    return 255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)));
  };
  return pack(f(0), f(8), f(4));
}
const toward = (h: number, to: number, by: number) => {
  const d = ((to - h + 540) % 360) - 180;
  return (h + Math.sign(d) * Math.min(Math.abs(d), by) + 360) % 360;
};

export interface Palette {
  /** Tones, packed 0xAABBGGRR. */
  readonly tone: Uint32Array;
  readonly lab: Float32Array;
  readonly ramp: Uint8Array;
  readonly step: Uint8Array;
  /** The next lighter / darker tone of the same ramp, or -1. */
  readonly up: Int16Array;
  readonly down: Int16Array;
  /** The tone's outline colour (selective outline), packed. */
  readonly line: Uint32Array;
  /** Tones per ramp, for the review. */
  readonly ramps: readonly (readonly number[])[];
}

function build(): Palette {
  const ground = Object.values(GROUND_RAMPS).map((r) => r.map(([a, b, c]) => [a, b, c] as RGB));
  const all: RGB[][] = [...MASTER, ...SCENERY].map((r) => r.map(fromHex)).concat(ground);
  const n = all.reduce((k, r) => k + r.length, 0);
  const tone = new Uint32Array(n);
  const lab = new Float32Array(n * 3);
  const ramp = new Uint8Array(n);
  const step = new Uint8Array(n);
  const up = new Int16Array(n).fill(-1);
  const down = new Int16Array(n).fill(-1);
  const line = new Uint32Array(n);
  const ramps: number[][] = [];
  let t = 0;
  all.forEach((r, ri) => {
    const ids: number[] = [];
    r.forEach(([R, G, B], si) => {
      tone[t] = pack(R, G, B);
      lab.set(oklab(R, G, B), t * 3);
      ramp[t] = ri;
      step[t] = si;
      if (si > 0) {
        down[t] = t - 1;
        up[t - 1] = t;
      }
      const [h, s, l] = toHsl(R, G, B);
      // Darker and a little cooler, never swung through red (warm hues lean only a few degrees), and muted.
      line[t] = fromHsl(
        s < 0.08 ? h : toward(h, 260, 8),
        Math.min(1, s * 0.75),
        Math.max(0.06, Math.min(l * 0.4, 0.2)),
      );
      ids.push(t++);
    });
    ramps.push(ids);
  });
  return { tone, lab, ramp, step, up, down, line, ramps };
}

let built: Palette | undefined;
/** The shared palette (built on first use). */
export const palette = (): Palette => (built ??= build());

/** Nearest tone per 24-bit colour, shared by every clean-up on this thread: a direct-mapped cache (a clash recomputes). */
const SLOTS = 1 << 16;
const memoKey = new Int32Array(SLOTS).fill(-1);
const memoTone = new Int16Array(SLOTS);

/** The nearest palette tone (OKLab distance) to a packed 0xAABBGGRR colour. */
export function nearestTone(p: number): number {
  const key = p & 0xffffff;
  const slot = (Math.imul(key, 0x9e3779b1) >>> 16) & (SLOTS - 1);
  if (memoKey[slot] === key) return memoTone[slot]!;
  const P = palette();
  const [L, A, B] = oklab(p & 255, (p >>> 8) & 255, (p >>> 16) & 255);
  let best = 0;
  let bd = Infinity;
  const lab = P.lab;
  for (let i = 0, n = P.tone.length; i < n; i++) {
    const dl = lab[i * 3]! - L;
    const da = lab[i * 3 + 1]! - A;
    const db = lab[i * 3 + 2]! - B;
    const d = dl * dl + da * da + db * db;
    if (d < bd) {
      bd = d;
      best = i;
    }
  }
  memoKey[slot] = key;
  memoTone[slot] = best;
  return best;
}
